export const voiceAgent = {
  header: {
    title: "AI Voice Agent",
    subBefore: "Answers your phone calls with real-time AI. Applies to",
    subEmphasis: "your entire account",
    active: "Active",
    inactive: "Inactive",
  },
  loading: "Loading configuration…",
  errors: {
    load: "Couldn't load the configuration.",
    save: "Couldn't save. Please try again.",
  },
  phone: {
    title: "Phone number",
    desc: "The number your guests call. A dedicated one is created for your account.",
    checking: "Checking your number…",
    pendingTitle: "Telephony pending activation",
    pendingText: "Number creation isn't enabled on your account yet. Get in touch and we'll turn it on.",
    activeCountry: "Active · country {country}",
    provisioning: "Provisioning… (may take a few minutes)",
    release: "Release",
    numberCountry: "Number country",
    searching: "Searching…",
    seeAvailable: "See available",
    perMonth: "{cost}/mo",
    getThis: "Get this number",
    getOne: "Get a number",
    confirmRelease: "Are you sure you want to release the number? It will stop receiving calls.",
    errNoneInCountry: "No numbers available in this country right now.",
    errTelnyxInactive: "Telephony isn't active on your account yet.",
    errSearchFailed: "Couldn't search for numbers.",
    errTelnyxInactiveContact: "Telephony isn't active on your account yet. Get in touch and we'll enable it.",
    errNoNumbersNow: "No numbers available right now.",
    errProvisionFailed: "Couldn't get the number.",
    errReleaseFailed: "Couldn't release the number.",
  },
  voiceLang: {
    title: "Voice and language",
    assistantVoice: "Assistant voice",
    recommended: "(recommended)",
    listenVoiceTitle: "Listen to the {voice} voice",
    loading: "Loading…",
    stop: "Stop",
    listen: "Listen",
    mainLanguage: "Main language",
    extraLanguages: "Additional languages it can speak",
    useIdentityTitle: "Use the chat agent's identity",
    useIdentityDesc: "Reuses the name, personality and tone you already set in General Settings.",
    voiceAssistantName: "Voice assistant name",
    namePlaceholder: "e.g. Málaga Centro Suites reception",
    greeting: "Greeting on pickup",
    optional: "(optional)",
    greetingPlaceholder: "If left empty, the assistant greets automatically with its identity.",
    errPlayback: "Couldn't play the sample.",
    errGenerate: "Couldn't generate the voice sample. Please try again.",
  },
  languages: {
    es: "Spanish",
    en: "English",
    fr: "French",
    pt: "Portuguese",
    it: "Italian",
    de: "German",
    ar: "Arabic",
  },
  countries: {
    ES: "Spain",
    US: "United States",
    GB: "United Kingdom",
    FR: "France",
    IT: "Italy",
    DE: "Germany",
    PT: "Portugal",
    CO: "Colombia",
    MX: "Mexico",
  },
  days: {
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
  },
  listings: {
    title: "Accommodations it answers for",
    desc: "Choose which accommodations the voice agent answers calls for.",
    all: "All accommodations",
    some: "Only some",
    scopeNoteBefore: "It answers for",
    scopeNoteStrong: "all",
    scopeNoteAfter: "of the account's accommodations, including any you add in the future.",
    searchPlaceholder: "Search accommodation…",
    selectAll: "Select all",
    clearAll: "Clear all",
    loadingListings: "Loading accommodations…",
    noListings: "This account has no accommodations.",
    noMatches: "No matches.",
    noneWarn: "If you don't select any, the agent will answer for all of them.",
    allDesc: "Including any you add later.",
    someDesc: "Only the ones you tick below.",
    selectedShort: "{n} of {total}",
  },
  capabilities: {
    title: "What it can do",
    knowledgeTitle: "Where it gets its information",
    knowledgeDesc: "Choose what the assistant checks when someone asks about the listing.",
    messagesTitle: "Take messages",
    messagesDesc: "If it doesn't know something, it takes the guest's message and notifies you.",
    linksTitle: "Send links after the call",
    linksDesc: "After hanging up, it sends the useful link (check-in or booking) via WhatsApp when relevant.",
    photosTitle: "Send equipment and amenity photos",
    photosDesc: "If the caller asks about an amenity that has photos, the agent sends them by WhatsApp to the number they are calling from.",
  },
  doc: {
    title: "Document with information",
    desc: "Upload a PDF or Word file and the agent will be able to answer using what's inside.",
    dropTitle: "Upload a document",
    dropHint: "PDF or Word (.docx), up to 15 MB",
    uploading: "Uploading the document…",
    replace: "Replace",
    remove: "Remove",
    chars: "{n} characters read",
    noText: "This document is scanned: it's images, so the text can't be read. Upload one with real text.",
    errType: "The file must be a PDF or Word (.docx).",
    errSize: "The file can't be larger than 15 MB.",
    errUpload: "The document couldn't be uploaded. Please try again.",
    errRemove: "The document couldn't be removed. Please try again.",
    fuenteBase: "The knowledge base",
    fuenteBaseDesc: "Your listings' information: check-in, amenities, rules and FAQ.",
    fuenteDoc: "A document of yours",
    fuenteDocDesc: "Only the PDF or Word you upload below, with its instructions.",
    fuenteAmbas: "Both",
    fuenteAmbasDesc: "The document first and, if the answer isn't there, the listing's information.",
    verTexto: "See what the assistant reads",
    abrirOriginal: "Open the document",
    previewTitle: "What the assistant reads from your document",
    previewSub: "This is the text pulled from the file. If something is missing here, the assistant doesn't know it either.",
    previewVacio: "No text could be pulled from this file.",
    cerrar: "Close",
    noUsado: "It's uploaded, but the assistant isn't using it right now: you chose to answer with the knowledge base only.",
    faltaDoc: "Upload a document to be able to choose it as a source.",
  },
  hours: {
    title: "Service hours",
    specific: "Specific hours",
    closed: "Closed",
    afterHours: "After hours",
    afterVoicemail: "Take a voice message",
    afterMessage: "Play a notice and hang up",
    afterMessagePlaceholder: "Message played after hours.",
  },
  recording: {
    title: "Recording and compliance",
    recordTitle: "Record calls",
    recordDesc: "Saves call audio so you can review it.",
    noticeTitle: "Announce recording",
    noticeDesc: "The assistant states at the start that the call may be recorded (recommended under GDPR).",
    transcribeTitle: "Transcribe calls",
    transcribeDesc: "Generates a text of each conversation.",
    transcribeNeedsRecord: "Transcripts are made from the audio: with “Record calls” off, none are generated.",
  },
  advanced: {
    title: "Advanced",
    maxDuration: "Maximum call duration",
    minutesOption: "{n} minutes",
    aiEngine: "AI engine",
  },
  save: {
    saved: "Saved ✓",
    save: "Save changes",
    dirty: "You have unsaved changes",
    clean: "Everything saved",
  },
  credits: {
    title: "Call minutes",
    desc: "Minutes included each month based on your properties. When they run out, top up anytime.",
    loading: "Loading balance…",
    minutesLeft: "min available",
    recharge: "Top up",
    includedThisMonth: "{used} of {total} included min used this month",
    purchased: "{min} min purchased",
    empty: "You've run out of minutes. Top up to keep receiving calls.",
    error: "Couldn't load the balance.",
    modal: {
      title: "Top up minutes",
      sub: "Pick a pack. It's a one-time payment and the minutes are added to your balance instantly.",
      pay: "Pay by card",
      secure: "Secure payment with Stripe.",
      close: "Close",
      error: "Couldn't start the payment. Please try again.",
    },
  },
};
